import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, Checkbox, FormControlLabel, CircularProgress, Typography } from '@mui/material';
import useJobExperienceForm from '../../hooks/useJobExperienceForm';
import { useQueryClient } from 'react-query';

const JobExperienceForm = ({ onCancel, id, cvId }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            title: '',
            company_title: '',
            description: '',
            start_date: '',
            end_date: '',
            isCurrentlyWorking: false
        }
    });

    const { isLoading, error, data, saveJobExperience } = useJobExperienceForm(id);

    useEffect(() => {
        if (data) {
            // Map backend field names to form field names
            const mappedData = {
                title: data.title || '',
                company_title: data.company_title || '',
                description: data.description || '',
                start_date: data.start_date ? new Date(data.start_date).toISOString().split('T')[0] : '',
                end_date: data.end_date ? new Date(data.end_date).toISOString().split('T')[0] : '',
                isCurrentlyWorking: !data.end_date
            };
            reset(mappedData);
        }
    }, [data, reset]);

    const isCurrentlyWorking = watch('isCurrentlyWorking');

    const formatDateForAPI = (dateStr) => {
        if (!dateStr) return null;
        return `${dateStr}T00:00:00Z`;
    };

    const handleFormSubmit = async (formData) => {
        try {
            // Map form field names to backend field names
            const backendData = {
                title: formData.title,
                company_title: formData.company_title,
                description: formData.description,
                start_date: formatDateForAPI(formData.start_date),
                end_date: formData.isCurrentlyWorking ? null : formatDateForAPI(formData.end_date),
                cv: cvId // Include the CV ID when saving
            };
            
            await saveJobExperience(backendData);
            queryClient.invalidateQueries(['jobExperiences', cvId ?? null]);
            onCancel();
        } catch (err) {
            // Error is handled by the hook and displayed in the form
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box className="space-y-4">
                {error && (
                    <Typography color="error" className="mb-4">
                        {error}
                    </Typography>
                )}

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Job Title"
                    {...register('title', { required: 'Job title is required' })}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Company Name"
                    {...register('company_title', { required: 'Company name is required' })}
                    error={!!errors.company_title}
                    helperText={errors.company_title?.message}
                />

                <TextField
                    fullWidth
                    className='mb-2'
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    {...register('start_date', { required: 'Start date is required' })}
                    error={!!errors.start_date}
                    helperText={errors.start_date?.message}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            {...register('isCurrentlyWorking')}
                        />
                    }
                    label="I am currently working here"
                />

                {!isCurrentlyWorking && (
                    <TextField
                        fullWidth
                        type="date"
                        className='mb-2'
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        {...register('end_date', { 
                            required: !isCurrentlyWorking ? 'End date is required' : false 
                        })}
                        error={!!errors.end_date}
                        helperText={errors.end_date?.message}
                    />
                )}

                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    className='mb-2'
                    label="Description"
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {id ? 'Update' : 'Add'} Job Experience
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default JobExperienceForm; 